<template>
  <Teleport to="body">
    <div class="chm-backdrop" @click.self="$emit('close')">
      <div class="chm">

        <!-- Header -->
        <div class="chm-head">
          <div class="chm-head-av" :style="{ background: avatarColor(contact.name) }">
            <img v-if="contact.thumbnail" :src="contact.thumbnail" :alt="contact.name"
                 @error="(e) => (e.target as HTMLImageElement).style.display='none'" />
            <span v-else>{{ initials(contact.name) }}</span>
          </div>
          <div>
            <div class="chm-head-name">{{ contact.name }}</div>
            <div class="chm-head-phone">{{ contact.phone }}</div>
          </div>
          <button class="asm-close" @click="$emit('close')">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="chm-body">
          <div v-if="loading" class="chm-loading">
            <div class="chm-spinner"></div>
            Carregando histórico…
          </div>
          <div v-else-if="error" class="chm-error">{{ error }}</div>
          <div v-else-if="!history.length" class="chm-empty">Nenhuma conversa encontrada para este contato.</div>
          <div v-else class="chm-list">
            <div v-for="c in history" :key="c.id" class="chm-row" @click="$emit('openChat', c)">
              <div class="chm-row-left">
                <span class="chm-row-id">#{{ c.id }}</span>
                <div class="chm-row-meta">
                  <span class="chm-row-date">{{ fmtDate(c.created_at) }}</span>
                  <span v-if="c.team" class="chm-row-team">{{ c.team.name }}</span>
                </div>
              </div>
              <div class="chm-row-center">
                <span v-if="c.assignee" class="chm-row-agent">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                  {{ c.assignee.name }}
                </span>
                <span v-if="c.labels.length" class="chm-row-labels">
                  <span v-for="l in c.labels.slice(0,3)" :key="l" class="label-chip">{{ l.replace(/_/g,' ') }}</span>
                </span>
              </div>
              <div class="chm-row-right">
                <span class="status-pill" :class="c.status" style="font-size:10px">
                  <span class="sdot"></span>{{ statusLabel(c.status) }}
                </span>
                <span class="chm-row-dur">{{ fmtDuration(c.resolved_at > 0 ? c.resolved_at - c.created_at : (Date.now() / 1000) - c.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!loading && history.length" class="chm-footer">
          {{ history.length }} conversa{{ history.length !== 1 ? 's' : '' }} encontrada{{ history.length !== 1 ? 's' : '' }}
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import type { Contact, Conversation } from '@/types/dashboard'
import { avatarColor, initials, fmtDuration } from '@/composables/useHelpers'

const props = defineProps<{
  contact:     Contact
  accountId:   number
  accessToken: string
}>()
defineEmits<{ close: []; openChat: [conv: Conversation] }>()

const loading = ref(true)
const error   = ref('')
const history = ref<Conversation[]>([])

onMounted(async () => {
  try {
    const res = await fetch(
      `/chatwoot-api/api/v1/accounts/${props.accountId}/contacts/${props.contact.id}/conversations`,
      { headers: { api_access_token: props.accessToken } }
    )
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    const raw: any[] = data?.payload ?? data ?? []
    history.value = raw
      .map((c: any) => ({
        id:            c.id,
        status:        c.status,
        channel:       c.channel,
        waiting_since: c.meta?.waiting_since ?? 0,
        unread_count:  c.unread_count ?? 0,
        created_at:    c.created_at,
        resolved_at:   c.meta?.resolved_at ?? 0,
        snoozed_until: c.snoozed_until ?? null,
        contact:       {
          id:        c.meta?.sender?.id ?? props.contact.id,
          name:      c.meta?.sender?.name ?? props.contact.name,
          phone:     props.contact.phone,
          thumbnail: c.meta?.sender?.thumbnail ?? props.contact.thumbnail,
        },
        assignee: c.meta?.assignee ? {
          id:           c.meta.assignee.id,
          name:         c.meta.assignee.name,
          email:        c.meta.assignee.email ?? '',
          role:         c.meta.assignee.role ?? 'agent',
          thumbnail:    c.meta.assignee.thumbnail ?? '',
          availability: c.meta.assignee.availability_status ?? 'offline',
        } : null,
        team:    c.meta?.team ? { id: c.meta.team.id, name: c.meta.team.name } : null,
        labels:  Array.isArray(c.labels) ? c.labels : [],
        fluxo_id: c.custom_attributes?.fluxo_id ?? null,
        priority: c.priority ?? null,
        cpf:  c.custom_attributes?.cpf  || null,
        cnpj: c.custom_attributes?.cnpj || null,
      }))
      .sort((a: Conversation, b: Conversation) => b.created_at - a.created_at)
  } catch (e: any) {
    error.value = 'Não foi possível carregar o histórico.'
  } finally {
    loading.value = false
  }
})

function statusLabel(s: string) {
  const map: Record<string, string> = { open: 'Aberta', resolved: 'Resolvida', pending: 'Pendente', snoozed: 'Adiada' }
  return map[s] || s
}

function fmtDate(ts: number) {
  if (!ts) return '—'
  return new Date(ts * 1000).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
</script>
